<script setup>
import { useMasterAuthStore } from "@/composables/store/master/useAuthStore";
import Logo from "@images/landing/logo.png";
import { VForm } from "vuetify/components/VForm";

definePage({
    meta: {
        layout: "blank",
        unauthenticatedOnly: true,
    },
});

// Composables
const router = useRouter();
const authStore = useMasterAuthStore();
const { signIn, updateSigninUserData, signin } = authStore;

// Refs
const isPasswordVisible = ref(false);
const refVForm = ref();
const errors = ref({
    email: undefined,
    password: undefined,
});

const user = ref({
    email: "",
    password: "",
    remember_me: false,
});

// Constants
const environment = import.meta.env.VITE_ENV === "development" ? "Development " : "";

// Methods
const onSubmit = async () => {
    const { valid } = (await refVForm.value?.validate()) || { valid: false };
    if (!valid) return;

    try {
        errors.value = { email: undefined, password: undefined };

        updateSigninUserData({
            email: user.value.email,
            password: user.value.password,
            remember_me: user.value.remember_me,
        });

        const response = await signIn();

        if (response) {
            try {
                // Change this to whatever your master landing page is
                await router.push("/master/dashboard/home");
            } catch (navigationError) {
                window.location.href = "/master/dashboard/home";
            }
        }
    } catch (error) {
        const msg =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "Login failed. Please try again.";

        errors.value = {
            email: msg,
            password: msg,
        };
    }
};
</script>

<template>
    <VRow no-gutters class="auth-wrapper bg-surface">
        <!-- Logo Section -->
        <VCol md="7" class="d-none d-md-flex auth-logo-section animate-fade-in">
            <div class="auth-logo-content">
                <VImg :src="Logo" class="auth-logo" />
                <p class="auth-logo-text text-brand">MARBLE SOLUTION</p>
            </div>
        </VCol>

        <!-- Login Form Section -->
        <VCol cols="12" md="5" class="auth-form-section">
            <VCard flat class="auth-form-card animate-slide-up">
                <VCardText class="auth-form-content">
                    <!-- Header -->
                    <div class="mb-4">
                        <h2 class="text-h2 mb-1">
                            {{ environment }}MARBLE ADMIN
                        </h2>
                        <p class="mb-0">Please sign-in to your master account</p>
                    </div>

                    <!-- Login Form -->
                    <VForm ref="refVForm" @submit.prevent="onSubmit">
                        <VRow>
                            <!-- Email Field -->
                            <VCol cols="12">
                                <AppTextField v-model="user.email" :disabled="signin.isLoading" label="Email"
                                    placeholder="master@email.com" type="email" autofocus
                                    :rules="[requiredValidator, emailValidator]" :error-messages="errors.email" />
                            </VCol>

                            <!-- Password Field -->
                            <VCol cols="12">
                                <AppTextField v-model="user.password" label="Password" :disabled="signin.isLoading"
                                    placeholder="············" :rules="[requiredValidator]"
                                    :type="isPasswordVisible ? 'text' : 'password'" :error-messages="errors.password"
                                    :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                                    @click:append-inner="isPasswordVisible = !isPasswordVisible" />

                                <!-- Remember Me and Forgot Password -->
                                <div class="d-flex align-center flex-wrap justify-space-between mt-1 mb-4">
                                    <VCheckbox v-model="user.remember_me" label="Remember me"
                                        :disabled="signin.isLoading" />

                                    <!-- Change route name to your master forgot password page route -->
                                    <RouterLink class="text-primary"
                                        :to="{ name: 'master-authentication-forgot-password' }">
                                        Forgot Password?
                                    </RouterLink>
                                </div>

                                <!-- Login Button -->
                                <VBtn color="primary" :loading="signin.isLoading" block type="submit">
                                    Login
                                </VBtn>
                            </VCol>
                        </VRow>
                    </VForm>
                </VCardText>
            </VCard>
        </VCol>
    </VRow>
</template>

<style lang="scss" scoped>
.auth-logo-section {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    margin: 0;
    background: #232323;
    color: white;
    min-block-size: 100vh;
}

.auth-logo {
    display: block;
    inline-size: 100%;
    max-block-size: 200px;
    max-inline-size: 280px;
    object-fit: contain;
}

.auth-logo-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0;
    gap: 0.75rem;
}

.auth-logo-text {
    margin: 0;
    font-size: clamp(1.5rem, 2vw + 1rem, 2.5rem);
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.auth-form-section {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgb(var(--v-theme-surface));
    min-block-size: 100vh;
}

.auth-form-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    background-color: rgb(var(--v-theme-surface));
    inline-size: 100%;
    overflow-y: auto;
}

.auth-form-content {
    align-items: center;
    inline-size: 100%;
    margin-block: 0;
    margin-inline: auto;
    max-inline-size: 500px;

    @media (max-width: 600px) {
        inline-size: 95%;
        padding-block: 1rem;
        padding-inline: 0;
    }
}

// Animation Keyframes
@keyframes fade-in {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes slide-up {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

// Animation Classes
.animate-fade-in {
    animation: fade-in 0.8s ease-in-out;
}

.animate-slide-up {
    animation: slide-up 0.6s ease-out;
}

// Interactive Elements Animations
.v-btn {
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 15%);
        transform: translateY(-2px);
    }
}

.v-text-field {
    transition: transform 0.2s ease;

    &:focus-within {
        transform: scale(1.01);
    }
}

.text-primary {
    transition: color 0.2s ease;

    &:hover {
        opacity: 0.85;
    }
}

.v-checkbox {
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.85;
    }
}
</style>
